import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Trash2, Eye, EyeOff, BarChart2, Crown } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  getSupabaseAdminClient,
  type ProductRecommendation,
  type RecommendationPlacement,
} from '@parentpath/supabase-client';
import { LocaleSelector } from '@/components/LocaleSelector';
import { useAdminLocale } from '@/hooks/useAdminLocale';
import { RecommendationForm } from './RecommendationForm';
import { RecommendationStatsModal } from './RecommendationStatsModal';
import { PLACEMENT_LABELS } from './constants';

const supabase = getSupabaseAdminClient();

export function RecommendationsPage() {
  const queryClient = useQueryClient();
  const { contentLocale } = useAdminLocale();
  const [isCreating, setIsCreating] = useState(false);
  const [editing, setEditing] = useState<ProductRecommendation | null>(null);
  const [statsFor, setStatsFor] = useState<ProductRecommendation | null>(null);

  const { data: recommendations, isLoading } = useQuery({
    queryKey: ['admin-recommendations', contentLocale],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('product_recommendations')
        .select('*')
        .eq('locale', contentLocale)
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as ProductRecommendation[];
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: Partial<ProductRecommendation>) => {
      const { error } = await (supabase.from('product_recommendations') as any).insert({
        ...data,
        locale: contentLocale,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-recommendations'] });
      setIsCreating(false);
      toast.success('Empfehlung erstellt');
    },
    onError: (error: Error) => toast.error(`Fehler: ${error.message}`),
  });

  const updateMutation = useMutation({
    mutationFn: async (data: ProductRecommendation) => {
      const { id, created_at: _ca, updated_at: _ua, created_by: _cb, ...payload } = data;
      void _ca; void _ua; void _cb;
      const { error } = await (supabase.from('product_recommendations') as any)
        .update(payload)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-recommendations'] });
      setEditing(null);
      toast.success('Empfehlung aktualisiert');
    },
    onError: (error: Error) => toast.error(`Fehler: ${error.message}`),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('product_recommendations').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-recommendations'] });
      toast.success('Empfehlung gelöscht');
    },
    onError: (error: Error) => toast.error(`Fehler: ${error.message}`),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await (supabase.from('product_recommendations') as any)
        .update({ is_active })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-recommendations'] });
    },
    onError: (error: Error) => toast.error(`Fehler: ${error.message}`),
  });

  const getScheduleLabel = (rec: ProductRecommendation): string => {
    const start = new Date(rec.start_date).toLocaleDateString('de-DE');
    const end = rec.end_date
      ? new Date(rec.end_date).toLocaleDateString('de-DE')
      : rec.duration_days
        ? `+${rec.duration_days}T`
        : '–';
    return `${start} → ${end}`;
  };

  const getTargetLabel = (rec: ProductRecommendation): string => {
    if (rec.target_mode === 'pregnancy') {
      const from = rec.ssw_from ?? 1;
      const to = rec.ssw_to ?? 42;
      return `SSW ${from}–${to}`;
    }
    const from = rec.baby_age_from_months ?? 0;
    const to = rec.baby_age_to_months ?? '∞';
    return `Baby ${from}–${to} Mo.`;
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/4"></div>
          <div className="h-16 bg-slate-200 rounded"></div>
          <div className="h-16 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Produkt-Empfehlungen</h1>
            <p className="text-slate-500 mt-1">
              Verwalte zeitlich gesteuerte, zielgruppen-spezifische Empfehlungen im User-App
            </p>
          </div>
          <div className="flex items-center gap-3">
            <LocaleSelector />
            <button
              onClick={() => setIsCreating(true)}
              className="flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Neue Empfehlung
            </button>
          </div>
        </div>
      </div>

      {isCreating && (
        <RecommendationForm
          onSubmit={(data) => createMutation.mutate(data)}
          onCancel={() => setIsCreating(false)}
          isLoading={createMutation.isPending}
        />
      )}

      {editing && (
        <RecommendationForm
          recommendation={editing}
          onSubmit={(data) => updateMutation.mutate({ ...editing, ...data })}
          onCancel={() => setEditing(null)}
          isLoading={updateMutation.isPending}
          isEditing
        />
      )}

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {recommendations && recommendations.length > 0 ? (
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Titel</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Platzierungen</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Zeitraum</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Ziel</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Prio</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {recommendations.map((rec) => (
                <tr key={rec.id} className={rec.is_active ? '' : 'opacity-50'}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {rec.image_url && rec.show_image && (
                        <img src={rec.image_url} alt="" className="w-8 h-8 rounded object-cover" />
                      )}
                      <div>
                        <div className="font-medium text-slate-900 text-sm">{rec.title}</div>
                        {rec.visible_to_premium && (
                          <span className="inline-flex items-center gap-1 text-xs text-amber-700">
                            <Crown className="w-3 h-3" /> auch Premium
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {rec.placements.map((p: RecommendationPlacement) => (
                        <span key={p} className="inline-block px-2 py-0.5 bg-slate-100 text-slate-700 text-xs rounded">
                          {PLACEMENT_LABELS[p]}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">{getScheduleLabel(rec)}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    <span className="font-medium">{rec.target_mode === 'pregnancy' ? 'Schwanger.' : 'Baby'}</span>
                    <div className="text-slate-500">{getTargetLabel(rec)}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{rec.priority}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setStatsFor(rec)}
                        className="p-2 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg"
                        title="Statistik"
                      >
                        <BarChart2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => toggleActiveMutation.mutate({ id: rec.id, is_active: !rec.is_active })}
                        className={`p-2 rounded-lg ${
                          rec.is_active ? 'text-green-600 hover:bg-green-50' : 'text-slate-400 hover:bg-slate-100'
                        }`}
                        title={rec.is_active ? 'Deaktivieren' : 'Aktivieren'}
                      >
                        {rec.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => setEditing(rec)}
                        className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"
                        title="Bearbeiten"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(`Empfehlung "${rec.title}" löschen?`)) {
                            deleteMutation.mutate(rec.id);
                          }
                        }}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        title="Löschen"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-8 text-center text-slate-500">Noch keine Empfehlungen vorhanden</div>
        )}
      </div>

      {statsFor && <RecommendationStatsModal recommendation={statsFor} onClose={() => setStatsFor(null)} />}
    </div>
  );
}
